import { DepositIcon, PiggyBankIcon, UserAddIcon } from "@xola/icons";
import { Button, Modal } from "@xola/ui-kit";
import clsx from "clsx";
import { isSettledByCancellationProtection } from "common";
import React, { ReactElement } from "react";
import { Except } from "type-fest";
import { Purchase } from "types";
import { isStoreCredit } from "../../models/Purchase";
import { CancellationProtectionRefundBlocked } from "../PurchaseModification/Refund/CancellationProtectionRefundBlocked";
import { RefundOption } from "./RefundOption";
import { useRefundOptions } from "./useRefundOptions";

const RefundOptionCopy: { [key in RefundOption]: string } = {
    [RefundOption.REDUCE]:
        "Use this to decrease or eliminate the Balance Due shown on the booking. This will not return any money to your customer",
    [RefundOption.REDUCE_AND_RETURN]: "Use this to provide ad-hoc discounts (e.g. bad weather, unhappy customer)",
    [RefundOption.RETURN_PAYMENT]:
        "Use this when you want to return some or all of the payment, but plan on collecting it back later (e.g. using a different credit card, split payments)",
    [RefundOption.CHANGE_GUEST_QUANTITY]:
        "Use this when you want reduce the guest quantity of this booking and optionally refund your customer",
    [RefundOption.CHANGE_EQUIPMENT_QUANTITY]:
        "Use this when you want reduce the equipment quantity of this booking and optionally refund your customer",
    [RefundOption.CHANGE_MERCHANDISE_QUANTITY]:
        "Use this when you want reduce the merchandise quantity of this booking and optionally refund your customer",
    [RefundOption.REFUND_PAYMENT]:
        "Use this when you have funded this store credit directly and you wish to return that payment",
    [RefundOption.REVERSE_FUNDING]:
        "Use this when you want to return funds back to the purchase from which this store credit was stored",
};

interface RefundOptionsModalProps {
    isOpen: boolean;
    purchase: Purchase;
    onClose: () => void;
    onConfirm: (option: RefundOption, purchase: Purchase) => void;
}

export const RefundOptionsModal = ({ isOpen, purchase, onClose, ...rest }: RefundOptionsModalProps) => {
    if (isSettledByCancellationProtection(purchase)) {
        return (
            <Modal isOpen={isOpen} size="huge" onClose={onClose}>
                <CancellationProtectionRefundBlocked onClose={onClose} />
            </Modal>
        );
    }

    return (
        <Modal isOpen={isOpen} size="huge" onClose={onClose}>
            <Modal.Header>Refund</Modal.Header>

            <Modal.Body>
                <p className="mb-4 text-xl font-semibold">Why Are You Refunding?</p>
                {isStoreCredit(purchase) ? (
                    <StoreCreditRefundOptions purchase={purchase} {...rest} />
                ) : (
                    <PurchaseRefundOptions purchase={purchase} {...rest} />
                )}
            </Modal.Body>

            <Modal.Footer className="space-x-2">
                <Button color="primary" onClick={onClose}>
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

interface RefundOptionItemProps {
    name: string;
    description: string;
    icon: ReactElement;
    onClick: () => void;
}

const RefundOptionItem = ({ name, description, icon, onClick, ...rest }: RefundOptionItemProps) => {
    return (
        <div
            className={clsx(
                "mb-4 flex cursor-pointer space-x-5 rounded border border-gray py-4 pr-6",
                "hover:border-black hover:bg-gray-lighter",
            )}
            onClick={onClick}
            {...rest}
        >
            <div className="flex items-center pr-2 pl-7 text-gray-darker">{icon}</div>

            <div className="text-left">
                <p className="pb-2 text-md font-semibold">{name}</p>
                <span className="text-gray-darker">{description}</span>
            </div>
        </div>
    );
};

const PurchaseRefundOptions = ({ purchase, onConfirm }: Except<RefundOptionsModalProps, "isOpen" | "onClose">) => {
    const options = useRefundOptions(purchase);
    const refundOption = options[0];
    const canReturnPayment = options.find((option) => option === RefundOption.RETURN_PAYMENT);
    const showChangeGuestOption = options.find((option) => option === RefundOption.CHANGE_GUEST_QUANTITY);
    const showChangeMerchandiseOption = options.find((option) => option === RefundOption.CHANGE_MERCHANDISE_QUANTITY);
    const showChangeEquipmentOption = options.find((option) => option === RefundOption.CHANGE_EQUIPMENT_QUANTITY);
    return (
        <>
            <RefundOptionItem
                name={refundOption}
                icon={<PiggyBankIcon size="large" />}
                description={RefundOptionCopy[refundOption]}
                onClick={() => onConfirm(refundOption, purchase)}
            />

            {canReturnPayment ? (
                <RefundOptionItem
                    name={RefundOption.RETURN_PAYMENT}
                    icon={<DepositIcon size="large" />}
                    description={RefundOptionCopy[RefundOption.RETURN_PAYMENT]}
                    onClick={() => onConfirm(RefundOption.RETURN_PAYMENT, purchase)}
                />
            ) : null}

            {showChangeGuestOption && (
                <RefundOptionItem
                    name={RefundOption.CHANGE_GUEST_QUANTITY}
                    icon={<UserAddIcon size="large" />}
                    description={RefundOptionCopy[RefundOption.CHANGE_GUEST_QUANTITY]}
                    onClick={() => onConfirm(RefundOption.CHANGE_GUEST_QUANTITY, purchase)}
                />
            )}

            {showChangeMerchandiseOption && (
                <RefundOptionItem
                    name={RefundOption.CHANGE_MERCHANDISE_QUANTITY}
                    icon={<UserAddIcon size="large" />}
                    description={RefundOptionCopy[RefundOption.CHANGE_MERCHANDISE_QUANTITY]}
                    onClick={() => onConfirm(RefundOption.CHANGE_MERCHANDISE_QUANTITY, purchase)}
                />
            )}

            {showChangeEquipmentOption && (
                <RefundOptionItem
                    name={RefundOption.CHANGE_EQUIPMENT_QUANTITY}
                    icon={<UserAddIcon size="large" />}
                    description={RefundOptionCopy[RefundOption.CHANGE_EQUIPMENT_QUANTITY]}
                    onClick={() => onConfirm(RefundOption.CHANGE_EQUIPMENT_QUANTITY, purchase)}
                />
            )}
        </>
    );
};

const StoreCreditRefundOptions = ({
    onConfirm,
    purchase,
}: {
    onConfirm: (option: RefundOption, purchase: Purchase) => void;
    purchase: Purchase;
}) => {
    return (
        <>
            <RefundOptionItem
                name={RefundOption.REVERSE_FUNDING}
                description={RefundOptionCopy[RefundOption.REVERSE_FUNDING]}
                icon={<DepositIcon size="large" />}
                onClick={() => onConfirm(RefundOption.REVERSE_FUNDING, purchase)}
            />
            <RefundOptionItem
                name={RefundOption.REFUND_PAYMENT}
                description={RefundOptionCopy[RefundOption.REFUND_PAYMENT]}
                icon={<PiggyBankIcon size="large" />}
                onClick={() => onConfirm(RefundOption.REFUND_PAYMENT, purchase)}
            />
        </>
    );
};
